import React, { useState } from 'react';
import {
    Menu,
    MenuItem,
    Checkbox,
    ListItemText,
    IconButton,
    Tooltip,
    Divider,
    Box,
} from '@material-ui/core';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ColumnConfig } from '../hooks/useColumnReorder';

interface ColumnSelectorProps {
    columns: ColumnConfig[];
    onColumnToggle: (columnId: string) => void;
    onSelectAll: () => void;
    onDeselectAll: () => void;
    onColumnReorder: (sourceIndex: number, destinationIndex: number) => void;
    buttonStyle?: React.CSSProperties;
}

/**
 * Reusable column selector component with drag and drop support
 */
const ColumnSelector: React.FC<ColumnSelectorProps> = ({
    columns,
    onColumnToggle,
    onSelectAll,
    onDeselectAll,
    onColumnReorder,
    buttonStyle = { float: 'right', marginTop: '-48px', marginRight: '270px' },
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDragEnd = (result: any) => {
        // Dropped outside the list
        if (!result.destination) {
            return;
        }

        // Call the parent handler to update column order
        onColumnReorder(result.source.index, result.destination.index);
    };

    return (
        <div style={buttonStyle}>
            <Tooltip title="Customize Columns">
                <IconButton onClick={handleMenuOpen} color="primary">
                    <ViewColumnIcon />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    style: {
                        maxHeight: 500,
                        width: 300,
                    },
                }}
            >
                <MenuItem onClick={onSelectAll}>
                    <ListItemText primary="Select All" />
                </MenuItem>
                <MenuItem onClick={onDeselectAll}>
                    <ListItemText primary="Deselect All" />
                </MenuItem>
                <Divider />
                <Box p={1}>
                    <small>Drag columns to reorder them in the grid</small>
                </Box>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="column-list">
                        {provided => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {columns.map((column, index) => (
                                    <Draggable
                                        key={column.id}
                                        draggableId={column.id}
                                        index={index}
                                        isDragDisabled={!column.visible}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                style={{
                                                    ...provided.draggableProps
                                                        .style,
                                                    background: snapshot.isDragging
                                                        ? '#f5f5f5'
                                                        : 'transparent',
                                                }}
                                            >
                                                <MenuItem
                                                    key={column.id}
                                                    onClick={() =>
                                                        onColumnToggle(
                                                            column.id
                                                        )
                                                    }
                                                >
                                                    <Checkbox
                                                        checked={column.visible}
                                                        color="primary"
                                                    />
                                                    <ListItemText
                                                        primary={column.label}
                                                    />
                                                    {column.visible && (
                                                        <div
                                                            {...provided.dragHandleProps}
                                                        >
                                                            <DragIndicatorIcon
                                                                color="action"
                                                                fontSize="small"
                                                            />
                                                        </div>
                                                    )}
                                                </MenuItem>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </Menu>
        </div>
    );
};

export default ColumnSelector;
